import _assign from "object-assign";

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React, { cloneElement, forwardRef, useCallback, useRef, useEffect, useState } from 'react';
import classnames from 'classnames';
import MuiTooltip from '@material-ui/core/Tooltip';
import PropTypes from 'prop-types';
import { deprecated } from 'prop-types-extra';
import { base } from '@jpmuitk/style';
import { useDensity, withStyles } from '@jpmuitk/theme';
import Popper from '@jpmuitk/popper';
import { useControlled } from '@jpmuitk/utils';
import useAriaAnnouncer from "@jpmuitk/aria-announcer/es/useAriaAnnouncer";
import { style } from './style';
import getIconForState from './getIconForState';
import State from './State'; // Keep in order of preference. First items are used as default

var PLACEMENT_PRIORITY = ['right', 'top', 'left', 'bottom'];
var Size = Object.freeze({
  medium: 'medium',
  small: 'small'
});
/**
 * Tooltips are containers that appear when the user hovers over an element.
 *
 * This component uses the render props pattern to enable the consumer to customise the
 * rendered content.
 *
 * The render prop callback is of the form
 *
 * @param {function} Icon returns the default Icon component
 * @param {function} getIconProps function callback that returns the icon props
 * @param {function} getTitleProps function callback that returns the title props
 *
 * @example
 * const TooltipExample = () => (
 *   <Tooltip
 *     render={({ Icon, getTitleProps }) => (
 *       <div>
 *         <Icon />
 *         <p {...getTitleProps() }>Title></p>
 *         Content to display
 *       </div>
 *     )}
 *     state={STATE}
 *   >
 *     <Button>Hover to Show Tooltip</Button>
 *   </Tooltip>
 * );
 */

var Tooltip = /*#__PURE__*/forwardRef(function Tooltip(props, ref) {
  var accessibleText = props.accessibleText,
      _props$PopperProps = props.PopperProps,
      PopperProps = _props$PopperProps === void 0 ? {} : _props$PopperProps,
      children = props.children,
      className = props.className,
      classes = props.classes,
      densityProp = props.density,
      disableArrow = props.disableArrow,
      _props$hasIcon = props.hasIcon,
      hasIcon = _props$hasIcon === void 0 ? true : _props$hasIcon,
      onClose = props.onClose,
      onOpen = props.onOpen,
      open = props.open,
      placement = props.placement,
      render = props.render,
      _props$size = props.size,
      size = _props$size === void 0 ? Size.MEDIUM : _props$size,
      _props$state = props.state,
      state = _props$state === void 0 ? 'info' : _props$state,
      title = props.title,
      rest = _objectWithoutProperties(props, ["accessibleText", "PopperProps", "children", "className", "classes", "density", "disableArrow", "hasIcon", "onClose", "onOpen", "open", "placement", "render", "size", "state", "title"]);

  var density = useDensity(densityProp);

  var _useControlled = useControlled({
    controlled: open,
    "default": false,
    name: 'Tooltip',
    state: 'open'
  }),
      _useControlled2 = _slicedToArray(_useControlled, 2),
      openState = _useControlled2[0],
      setOpen = _useControlled2[1];

  var _useState = useState(),
      titleRef = _useState[0],
      setTitleRef = _useState[1];

  var _useAriaAnnouncer = useAriaAnnouncer(),
      announce = _useAriaAnnouncer.announce;

  var openEvent = useRef();

  var handleOpen = function handleOpen(event) {
    openEvent.current = event.type;
    setOpen(true);
    onOpen && onOpen(event);
  };

  var handleClose = function handleClose(event) {
    setOpen(false);
    onClose && onClose(event);
  };

  var handleKeyDown = function handleKeyDown(event) {
    var childrenProps = children.props;

    if (childrenProps.onKeyDown) {
      childrenProps.onKeyDown(event);
    }

    if (event.key === 'Escape') {
      onClose && onClose(event);
      setOpen(false);
    }
  };

  var handleTitleRef = useCallback(function (instance) {
    setTitleRef(instance);
  }, []);
  useEffect(function () {
    if (openState && openEvent.current === 'mouseover' && titleRef) {
      // jsdom does not have innertext
      announce(titleRef.innerText); // Cannot assume accessible text is rendered in a tag

      if (render && accessibleText) {
        announce(accessibleText);
      }
    }
  }, [accessibleText, announce, openState, render, titleRef]);
  var getPopperProps = useCallback(function () {
    return _assign({
      keepMounted: true
    }, PopperProps, {
      className: classnames(classes.popper, size, state, className, PopperProps.className),
      role: null
    });
  }, [PopperProps, className, classes, size, state]);
  var getIconProps = useCallback(function () {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var iconClassName = _ref.className,
        iconRest = _objectWithoutProperties(_ref, ["className"]);

    return _assign({
      classes: {
        root: classnames(iconClassName, classes.stateIcon, state),
        content: classes.stateIconContent
      },
      size: 12,
      accessibleText: state
    }, iconRest);
  }, [classes, state]);
  var getIcon = useCallback(function (passedProps) {
    if (!hasIcon) {
      return null;
    }

    var icon = getIconForState(state);
    return icon ? icon(getIconProps(passedProps)) : null;
  }, [state, getIconProps, hasIcon]);
  var getTitleProps = useCallback(function () {
    var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    var titleClassName = _ref2.className,
        titleRest = _objectWithoutProperties(_ref2, ["className"]);

    return _assign({
      className: classnames(classes.title, titleClassName)
    }, titleRest);
  }, [classes]);
  var getTitle = useCallback(function () {
    if (!render && !title) {
      return '';
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classnames(classes.tooltipContentRoot, _defineProperty({}, classes.stateIconContainer, state)),
      ref: handleTitleRef
    }, render ? render({
      Icon: function Icon(passedProps) {
        return getIcon(passedProps);
      },
      getIconProps: getIconProps,
      getTitleProps: getTitleProps
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, getIcon(), /*#__PURE__*/React.createElement("span", {
      className: classes.body
    }, title), /*#__PURE__*/React.createElement("span", {
      style: base.accessibleText
    }, accessibleText))), !disableArrow && /*#__PURE__*/React.createElement("div", {
      className: classes.arrow,
      "x-arrow": ""
    }));
  }, [render, title, classes.tooltipContentRoot, classes.stateIconContainer, classes.body, classes.arrow, state, handleTitleRef, getIconProps, getTitleProps, getIcon, accessibleText, disableArrow]);
  var densityClassName = classes["".concat(density, "Density")];
  return /*#__PURE__*/React.createElement(MuiTooltip, _assign({
    PopperComponent: Popper,
    PopperProps: getPopperProps(),
    classes: {
      tooltip: classnames(classes.tooltipRoot, classes[size || Size.medium], classes[state], densityClassName),
      tooltipPlacementLeft: classes.left,
      tooltipPlacementRight: classes.right,
      tooltipPlacementBottom: classes.bottom,
      tooltipPlacementTop: classes.top
    },
    interactive: true,
    onClose: handleClose,
    onOpen: handleOpen,
    open: open != null ? open : openState,
    placement: placement || PLACEMENT_PRIORITY[0],
    ref: ref,
    title: getTitle()
  }, rest), /*#__PURE__*/cloneElement(children, {
    onKeyDown: handleKeyDown
  }));
});
process.env.NODE_ENV !== "production" ? Tooltip.propTypes = {
  /**
   * @external - material-ui / Popper.js
   * Properties applied to Material-UI's Popper (https://material-ui.com/api/popper) element.
   * By Default we have 'PopperProps.keepMounted' as true - it can be set to false but can cause
   * Accessibility screenreader issues
   */
  // eslint-disable-next-line react/forbid-prop-types
  PopperProps: PropTypes.object,

  /**
   * @deprecated
   * optional label used for accessible information when using a render prop instead of string title
   */
  accessibleText: deprecated(PropTypes.string, 'Tooltip text is inherently accessible, please use `title` or `render` instead'),

  /**
   * A required node on which the tooltip is triggered
   */
  children: PropTypes.node.isRequired,

  /**
   * The className(s) of the component
   */
  className: PropTypes.string,

  /**
   * @external - material-ui
   * Class selectors required by theme
   */
  classes: PropTypes.objectOf(PropTypes.string),

  /**
   * The density of a component affects the style of the layout.
   * A high density component uses minimal sizing and spacing to convey the intended UI design.
   * Conversely, a low density component, maximizes the use of space and size to convey the UI Design.
   */
  density: PropTypes.oneOf(['touch', 'low', 'medium', 'high']),

  /**
   * Removes the tooltip arrow.
   */
  disableArrow: PropTypes.bool,

  /**
   * @external - material-ui
   * The number of milliseconds to wait before showing the tooltip.
   */
  enterDelay: PropTypes.number,

  /**
   * Whether to show a state icon within the tooltip
   * DEFAULT HAS_ICON : true
   */
  hasIcon: PropTypes.bool,

  /**
   * @external - material-ui
   * The number of milliseconds to wait before hidding the tooltip.
   */
  leaveDelay: PropTypes.number,

  /**
   * @external - material-ui
   * Callback fired when the tooltip requests to be closed.
   *
   * @param {object} event The event source of the callback
   */
  onClose: PropTypes.func,

  /**
   * @external - material-ui
   * Callback fired when the tooltip requests to be open.
   *
   * @param {object} event The event source of the callback
   */
  onOpen: PropTypes.func,

  /**
   * @external - material-ui
   * If `true`, the tooltip is shown.
   */
  open: PropTypes.bool,

  /**
   * @external - material-ui
   * Tooltip placement ['right', 'top', 'left', 'bottom']
   */
  placement: PropTypes.oneOf(PLACEMENT_PRIORITY),

  /**
   * A callback function to render the tooltip content
   * @param {function} getIcon getter for the icon based on the state
   * @param {function} getIconProps getter for the icon properties
   * @param {function} getTitleProps getter for the title properties
   */
  render: PropTypes.func,

  /**
   * A string to determine the size of the tooltip
   * DEFAULT SIZE: 'medium'
   */
  size: PropTypes.oneOf(Object.keys(Size)),

  /**
   * A string to determine the current state of the tooltip
   * DEFAULT STATE: 'base'
   */
  state: PropTypes.oneOf(Object.keys(State)),

  /**
   * A React node (such as a string) to represent tooltip title
   */
  title: PropTypes.node
} : void 0; // TODO (BREAKING CHANGE REQUIRED)
// https://issuetracking.jpmchase.net/jira12/browse/JPMUITK-2835
// Add unique JPMUI prefix to name to identify component as a Toolkit component

export default withStyles(style, {
  name: 'Tooltip'
})(Tooltip);